/**
 * OCTOPUS Dashboard - FASE 3: Widget Library
 * MapView - Widget de mapa GPS com track trail e geofences
 * 
 * Estilo: Marítimo/Industrial moderno, tema dark
 * 
 * IMPORTANTE: Este componente é PASSIVO - recebe todos os dados via props.
 * NÃO faz fetch de dados internamente.
 */

import React, { useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MapContainer, TileLayer, Marker, Polyline, useMap, LayersControl, Polygon, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const { BaseLayer } = LayersControl;

// ============================================================================
// CUSTOM MARKER - Ícone SVG para equipamentos
// ============================================================================

/**
 * Cria um ícone customizado para o marker do equipamento
 * @param {boolean} isOnline - Status online/offline do equipamento
 * @returns {L.DivIcon} Ícone Leaflet
 */
const createEquipmentMarker = (isOnline = true) => {
  const color = isOnline ? '#22c55e' : '#64748b';
  const bgColor = isOnline ? 'rgba(34, 197, 94, 0.2)' : 'rgba(100, 116, 139, 0.2)';
  
  return L.divIcon({
    className: 'custom-equipment-marker',
    html: `
      <div style="
        width: 36px;
        height: 36px;
        background: ${bgColor};
        border: 2px solid ${color};
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 0 10px ${color}40;
      ">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/>
          <path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76"/>
          <path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"/>
          <path d="M12 10v4"/>
          <path d="M12 2v3"/>
        </svg>
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18],
  });
};

const createWaypointMarker = (color = '#0EA5E9') => L.divIcon({
  className: 'map-widget-geofence-waypoint',
  html: `
    <div style="
      width: 12px;
      height: 12px;
      border-radius: 9999px;
      border: 2px solid ${color};
      background: rgba(2, 6, 23, 0.95);
      box-shadow: 0 0 0 2px rgba(255,255,255,0.12);
    "></div>
  `,
  iconSize: [12, 12],
  iconAnchor: [6, 6],
  popupAnchor: [0, -8],
});

const getGeofencePathOptions = (gf, fallbackOpacity = 0.15) => {
  const fillEnabled = gf?.fill_enabled !== false;
  const fillOpacity = Number.isFinite(Number(gf?.fill_opacity))
    ? Number(gf.fill_opacity)
    : fallbackOpacity;
  return {
    color: gf?.border_color || gf?.color || '#0EA5E9',
    fillColor: gf?.color || '#0EA5E9',
    fillOpacity: fillEnabled ? fillOpacity : 0,
    weight: Number(gf?.border_width || 1),
  };
};

// ============================================================================
// MAP FOLLOWER - Componente para centralizar mapa uma única vez
// ============================================================================

/**
 * Centraliza o mapa na posição inicial e quando resetCenter muda
 */
const MapFollower = ({ position, resetCenter }) => {
  const map = useMap();
  const hasCenteredRef = useRef(false);
  
  useEffect(() => {
    // Centraliza apenas uma vez quando posição se torna disponível
    if (position && !hasCenteredRef.current) {
      map.setView(position, map.getZoom());
      hasCenteredRef.current = true;
    }
  }, [position, map]);
  
  // Re-centraliza quando resetCenter muda (ex: botão "Centralizar")
  useEffect(() => {
    if (resetCenter && position) {
      map.setView(position, map.getZoom());
    }
  }, [resetCenter, position, map]);
  
  return null;
};

// ============================================================================
// MAP RESIZER - Componente para lidar com resize do container (bug do Leaflet)
// ============================================================================

/**
 * Observa mudanças no tamanho do container e chama invalidateSize()
 * Necessário porque o Leaflet não detecta resize de containers flexíveis/grid
 */
const MapResizer = ({ containerRef }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!containerRef?.current) return;
    
    const resizeObserver = new ResizeObserver(() => {
      // Pequeno delay para garantir que o DOM foi atualizado
      setTimeout(() => {
        map.invalidateSize();
      }, 100);
    });
    
    resizeObserver.observe(containerRef.current);
    
    // Também invalida no mount (caso o mapa seja criado em container já visível)
    setTimeout(() => {
      map.invalidateSize();
    }, 200);
    
    return () => {
      resizeObserver.disconnect();
    };
  }, [map, containerRef]);
  
  return null;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const MapView = ({
  // Posição atual
  latitude,
  longitude,
  // Status do equipamento
  isOnline = false,
  equipmentName = '',
  // Trail (histórico de posições)
  trackPositions = [],
  // Geofences overlay
  geofences = [],
  // Configuração
  zoom = 14,
  height = '100%',
  showLayerControl = true,
  // Label (para consistência com outros widgets)
  label = '',
  // Callbacks opcionais
  onMarkerClick,
  // Controle de centralização
  resetCenter,
}) => {
  // Ref para o container do mapa (necessário para ResizeObserver)
  const containerRef = useRef(null);
  
  // Memoiza a posição atual
  const position = useMemo(() => {
    if (latitude != null && longitude != null && !isNaN(latitude) && !isNaN(longitude)) {
      return [latitude, longitude];
    }
    return null;
  }, [latitude, longitude]);

  // Memoiza o marker icon
  const markerIcon = useMemo(() => createEquipmentMarker(isOnline), [isOnline]);

  // Memoiza as posições do trail (já vem como [[lat, lon], ...])
  const memoizedTrack = useMemo(() => {
    if (!Array.isArray(trackPositions)) return [];
    return trackPositions.filter(pos => 
      Array.isArray(pos) && 
      pos.length >= 2 && 
      !isNaN(pos[0]) && 
      !isNaN(pos[1])
    );
  }, [trackPositions]);

  // Memoiza as geofences convertidas (GeoJSON [lon, lat] → Leaflet [lat, lon])
  const memoizedGeofences = useMemo(() => {
    if (!Array.isArray(geofences)) return [];
    return geofences
      .map(gf => {
        const geometryType = gf?.geometry_type || (gf?.point ? 'waypoint' : 'polygon');

        if (geometryType === 'polygon' && gf?.polygon?.coordinates?.[0]) {
          const positions = gf.polygon.coordinates[0].map(([lon, lat]) => [lat, lon]);
          return {
            ...gf,
            geometryType,
            positions,
          };
        }

        if (
          geometryType === 'waypoint' &&
          Array.isArray(gf?.point?.coordinates) &&
          gf.point.coordinates.length >= 2
        ) {
          const [lon, lat] = gf.point.coordinates;
          return {
            ...gf,
            geometryType,
            position: [lat, lon],
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [geofences]);

  // Se não há posição, mostra placeholder
  if (!position) {
    return (
      <div 
        className="flex flex-col items-center justify-center bg-gray-900 rounded-lg border border-gray-700"
        style={{ height, minHeight: 200 }}
      >
        <div className="text-gray-500 text-center p-4">
          <svg 
            className="w-12 h-12 mx-auto mb-2 opacity-50" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" 
            />
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" 
            />
          </svg>
          <p className="text-sm">GPS não disponível</p>
          {label && <p className="text-xs text-gray-600 mt-1">{label}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full">
      {/* Label (se fornecido) */}
      {label && (
        <div className="text-gray-300 font-medium text-sm px-2 py-1 bg-gray-900/50">
          {label}
        </div>
      )}
      
      {/* Mapa */}
      <div 
        ref={containerRef}
        className="flex-1 rounded-lg overflow-hidden border border-gray-700"
        style={{ height: label ? `calc(${height} - 32px)` : height, minHeight: 180 }}
        data-testid="map-view-container"
      >
        <MapContainer
          center={position}
          zoom={zoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          {/* Resize Observer para invalidateSize */}
          <MapResizer containerRef={containerRef} />
          
          {/* Layer Control */}
          {showLayerControl ? (
            <LayersControl position="topright">
              <BaseLayer checked name="Mapa Padrão">
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; OpenStreetMap'
                />
              </BaseLayer>
              <BaseLayer name="Mapa Escuro">
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; CARTO'
                />
              </BaseLayer>
              <BaseLayer name="Satélite">
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution='&copy; Esri'
                />
              </BaseLayer>
            </LayersControl>
          ) : (
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap'
            />
          )}
          
          {/* Map Follower (centralização automática) */}
          <MapFollower position={position} resetCenter={resetCenter} />
          
          {/* Geofences Overlay (polygon + waypoint) */}
          {memoizedGeofences.map((gf, index) => {
            if (gf.geometryType === 'waypoint' && gf.position) {
              return (
                <Marker
                  key={`geofence-waypoint-${gf.id || index}`}
                  position={gf.position}
                  icon={createWaypointMarker(gf.color || '#0EA5E9')}
                >
                  <Popup>
                    <div className="text-sm font-semibold flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: gf.color || '#0EA5E9' }}
                      />
                      {gf.name || 'Waypoint'}
                    </div>
                  </Popup>
                </Marker>
              );
            }

            return (
              <Polygon
                key={`geofence-${gf.id || index}`}
                positions={gf.positions}
                pathOptions={{
                  ...getGeofencePathOptions(gf, 0.15)
                }}
              >
                <Popup>
                  <div className="text-sm font-semibold flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: gf.color || '#0EA5E9' }}
                    />
                    {gf.name || 'Geofence'}
                  </div>
                </Popup>
              </Polygon>
            );
          })}
          
          {/* Track Trail (Polyline) */}
          {memoizedTrack.length > 1 && (
            <Polyline
              key="equipment-trail"
              positions={memoizedTrack}
              pathOptions={{
                color: '#3b82f6',
                weight: 2,
                opacity: 0.6,
              }}
            />
          )}
          
          {/* Current Position Marker */}
          <Marker 
            position={position}
            icon={markerIcon}
            eventHandlers={{
              click: onMarkerClick,
            }}
          >
            <Popup>
              <div className="text-sm">
                <strong>{equipmentName || 'Equipamento'}</strong>
                <br />
                <span className="text-gray-500">
                  {latitude.toFixed(5)}, {longitude.toFixed(5)}
                </span>
              </div>
            </Popup>
          </Marker>
        </MapContainer>
      </div>
    </div>
  );
};

// ============================================================================
// PROP TYPES
// ============================================================================

MapView.propTypes = {
  // Posição atual
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  // Status do equipamento
  isOnline: PropTypes.bool,
  equipmentName: PropTypes.string,
  // Trail - array de [lat, lon]
  trackPositions: PropTypes.arrayOf(
    PropTypes.arrayOf(PropTypes.number)
  ),
  // Geofences - array de objetos com polygon.coordinates ou point.coordinates
  geofences: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      color: PropTypes.string,
      geometry_type: PropTypes.oneOf(['polygon', 'waypoint']),
      polygon: PropTypes.shape({
        coordinates: PropTypes.array,
      }),
      point: PropTypes.shape({
        coordinates: PropTypes.array,
      }),
    })
  ),
  // Config
  zoom: PropTypes.number,
  height: PropTypes.string,
  showLayerControl: PropTypes.bool,
  // Label
  label: PropTypes.string,
  // Callbacks
  onMarkerClick: PropTypes.func,
  // Controle de centralização
  resetCenter: PropTypes.any,
};

MapView.defaultProps = {
  isOnline: false,
  equipmentName: '',
  trackPositions: [],
  geofences: [],
  zoom: 14,
  height: '100%',
  showLayerControl: true,
  label: '',
};

export default MapView;
